import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { type InfiniteData, useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'

import { GroupMemberRole, type GroupListItemWithEmbeddedMembership } from '../../groups-layer/types/types'
import { groupsQueryKeys } from '../../groups-layer/utils/query-keys.utils'
import { CommunityUserService } from '../services/community-user.service'
import { communityUserQueryKeys } from '../utils/query-keys.utils'
import { transformToCommunityUser, updateCommunityUserCache } from '../utils/cache-update.utils'
import { GokollabCommunityUserService } from '../services/gokollab-community-user.service'
import type { MemberStatus } from '../../groups-layer/types/types'
import type { QueryClient } from '@tanstack/vue-query'
import type {
  CommonGroupsQueryParams,
  FollowMutationVariables,
  FollowStatusError,
  FollowStatusMutationContext,
  FollowUsersQueryParams,
  FollowStatusQueryParams,
  CaptureGroupPaymentMutationVariables,
  GroupPaymentMutationVariables,
  JoinGroupMutationVariables,
} from './community-user.query-store.types'

import type {
  CommunityUserGroup,
  CommonGroupsResponse,
  IGroup,
  UserActivityStats,
  FollowUser,
  UserDetailsResponse,
  UserGroupsResponse,
  UserGroupsQueryParams,
  UserContributionsResponse,
  UserContributionsQueryParams,
} from '../types/community-user'

/** Read user group details from default groups list cache only (client-only). Used as placeholder while API fetches. */
function getUserGroupDetailsFromListCache(queryClient: QueryClient, groupId: string, locationId: string): CommunityUserGroup | null {
  if (process.server) return null
  const data = queryClient.getQueryData(groupsQueryKeys.groups()) as InfiniteData<GroupListItemWithEmbeddedMembership[]> | undefined
  if (!data?.pages) return null
  const listItem = data.pages.flat().find((g: GroupListItemWithEmbeddedMembership) => g._id === groupId)
  const detail = listItem?.userGroupDetails ?? listItem?.userGroupDetail ?? listItem?.['user-group-detail']
  if (!detail?.role || !detail?.status) return null
  const detailLocationId = detail.locationId ?? listItem?.locationId
  if (detailLocationId !== locationId) return null
  return {
    _id: detail._id ?? '',
    contactId: detail.contactId ?? '',
    groupId: detail.groupId ?? groupId,
    locationId: detailLocationId ?? locationId,
    role: detail.role as GroupMemberRole,
    status: detail.status as MemberStatus,
    createdAt: '',
    updatedAt: '',
  }
}

/**
 * Community User Query Store - Handles fetching user profile data using TanStack Query
 */
export const useCommunityUserQueryStore = () => {
  const service = new CommunityUserService()
  const gokollabService = new GokollabCommunityUserService()
  const queryClient = useQueryClient()
  const { store: authStore } = useAuth()
  // ===== QUERY KEY FACTORY =====
  const queryKeys = communityUserQueryKeys

  const getErrorStatusCode = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') {
      return undefined
    }

    const maybeError = error as FollowStatusError

    return maybeError.response?.status ?? maybeError.statusCode ?? maybeError.status
  }

  // ===== QUERY CREATION FUNCTIONS =====

  // User group details query: uses default groups list cache as placeholder so UI can show partial data while API fetches.
  const fetchCurrentUserDetailsForGroupQuery = (
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>,
    isAuthenticated: MaybeRefOrGetter<boolean> = true
  ) => {
    const query = useQuery<CommunityUserGroup, Error>({
      queryKey: computed(() => {
        const id = toValue(groupId)
        const locId = toValue(locationId)
        return queryKeys.userGroupDetails(id || '', locId || '')
      }),
      queryFn: async () => {
        const locId = toValue(locationId)
        const id = toValue(groupId)
        if (!id) {
          throw new Error('Group ID is required for fetching user group details')
        }
        if (!locId) {
          throw new Error('Location ID is required for fetching user group details')
        }
        return service.getUserDetailsForCurrentGroup(id, locId)
      },
      placeholderData: (): CommunityUserGroup | undefined => {
        const id = toValue(groupId)
        const locId = toValue(locationId)
        if (!id || !locId) return undefined
        return getUserGroupDetailsFromListCache(queryClient, id, locId) ?? undefined
      },
      staleTime: 5 * 60 * 1000, // 5 minutes for user group details
      gcTime: 10 * 60 * 1000, // 10 minutes
      enabled: computed(() => !!toValue(groupId) && !!toValue(locationId) && !!toValue(isAuthenticated)),
      refetchOnWindowFocus: false,
    })

    // Add computed properties for better state management
    const isEnabled = computed(() => !!toValue(groupId) && !!toValue(locationId) && !!toValue(isAuthenticated))

    return {
      ...query,
      isEnabled,
    }
  }

  // User group details query by contact ID
  const fetchUserDetailsForGroupByContactQuery = (
    contactId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined>,
    locationId: MaybeRefOrGetter<string | undefined>
  ) => {
    const query = useQuery<CommunityUserGroup, Error>({
      queryKey: computed(() => {
        const contact = toValue(contactId)
        const id = toValue(groupId)
        const locId = toValue(locationId)
        return queryKeys.userGroupDetailsByContact(contact || '', id || '', locId || '')
      }),
      queryFn: async () => {
        const contact = toValue(contactId)
        const id = toValue(groupId)
        const locId = toValue(locationId)
        if (!contact) {
          throw new Error('Contact ID is required for fetching user group details')
        }
        if (!id) {
          throw new Error('Group ID is required for fetching user group details')
        }
        if (!locId) {
          throw new Error('Location ID is required for fetching user group details')
        }
        return service.getUserDetailsForGroupUser(contact, id, locId)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      enabled: computed(() => !!toValue(contactId) && !!toValue(groupId) && !!toValue(locationId)),
      refetchOnWindowFocus: false,
    })

    const isEnabled = computed(() => !!toValue(contactId) && !!toValue(groupId) && !!toValue(locationId))

    return {
      ...query,
      isEnabled,
    }
  }

  // User details query by slug
  const fetchUserDetailsBySlugQuery = (slug: MaybeRefOrGetter<string>) => {
    return useQuery<UserDetailsResponse, Error>({
      queryKey: computed(() => {
        const p = toValue(slug)
        const key = queryKeys.userBySlug(toValue(p))
        return key
      }),
      queryFn: async () => {
        const slugValue = toValue(slug)
        if (!slugValue) {
          throw new Error('User slug is required for fetching user details')
        }
        return service.getUserDetailsBySlug(slugValue)
      },
      enabled: computed(() => !!toValue(slug)),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    })
  }

  // User groups infinite query by contact ID
  const fetchUserGroupsInfiniteQuery = (params: MaybeRefOrGetter<Omit<UserGroupsQueryParams, 'offset'>>) => {
    const infiniteQuery = useInfiniteQuery<UserGroupsResponse, Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return [
          ...queryKeys.userGroups(
            toValue(p.contactId),
            0, // offset will be handled by pageParam
            toValue(p.limit) || 10,
            Boolean(toValue(p.skipPagination)),
            toValue(p.role)
          ),
          'infinite',
        ]
      }),
      queryFn: async ({ pageParam = 0 }) => {
        const p = toValue(params)
        const contactId = toValue(p.contactId)
        if (!contactId) {
          throw new Error('Contact ID is required for fetching user groups')
        }
        return service.getUserGroups(
          contactId,
          pageParam as number,
          toValue(p.limit) || 30,
          Boolean(toValue(p.skipPagination)),
          toValue(p.role)
        )
      },
      enabled: computed(() => {
        const p = toValue(params)
        return !!p.contactId && !!authStore.isLoggedIn
      }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.reduce((sum, page) => sum + page.groups.length, 0)
        return totalFetched < lastPage.total ? totalFetched : undefined
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    })

    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flatMap(page => page.groups)
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  // User common groups query
  const fetchCommonGroupsQuery = (params: MaybeRefOrGetter<CommonGroupsQueryParams>) => {
    return useQuery<CommonGroupsResponse, Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return queryKeys.commonGroups(toValue(p.contactId))
      }),
      queryFn: async () => {
        const p = toValue(params)
        const contactId = toValue(p.contactId)
        if (!contactId) {
          throw new Error('Contact ID is required for fetching common groups')
        }
        return service.getCommonGroups(contactId)
      },
      enabled: computed(() => {
        const p = toValue(params)
        return p.enabled !== false && !!p.contactId
      }),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: true,
    })
  }

  const fetchUserAccessibleGroupsQuery = (params: MaybeRefOrGetter<{ contactId?: string; enabled?: boolean }>) => {
    return useQuery<Partial<IGroup>[], Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return ['community-user', 'accessible-groups', p.contactId || ''] as const
      }),
      queryFn: async () => {
        const p = toValue(params)
        const contactId = p.contactId
        if (!contactId) {
          return []
        }

        return service.getUserAccessibleGroups(contactId)
      },
      enabled: computed(() => {
        const p = toValue(params)
        return p.enabled !== false && !!p.contactId
      }),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    })
  }

  // Follow status query for a target contact
  const fetchFollowStatusQuery = (params: MaybeRefOrGetter<FollowStatusQueryParams>) => {
    return useQuery<boolean, Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return queryKeys.followStatus(p.contactId || '', p.groupId)
      }),
      queryFn: async () => {
        const p = toValue(params)
        const id = p.contactId
        if (!id) {
          return false
        }

        try {
          const followInfo = await service.getFollowInfo(id, p.groupId)
          return !!followInfo
        } catch (error) {
          const statusCode = getErrorStatusCode(error)

          // Backend returns not found when there is no follow relationship.
          if (statusCode === 404) {
            return false
          }

          throw error
        }
      },
      enabled: computed(() => {
        const p = toValue(params)
        return p.enabled !== false && !!p.contactId
      }),
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
    })
  }

  const fetchFollowersQuery = (params: MaybeRefOrGetter<FollowUsersQueryParams>) => {
    return useQuery<FollowUser[], Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return queryKeys.followers(p.contactId || '', p.locationId || '', p.limit || 10, p.pageNo || 1)
      }),
      queryFn: async () => {
        const p = toValue(params)
        if (!p.contactId) {
          return []
        }
        if (!p.locationId) {
          throw new Error('Location ID is required for fetching followers')
        }
        return service.getFollowersUsers(p.contactId, p.limit || 10, p.pageNo || 1, p.locationId)
      },
      enabled: computed(() => {
        const p = toValue(params)
        return p.enabled !== false && !!p.contactId && !!p.locationId
      }),
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    })
  }

  const fetchFollowingQuery = (params: MaybeRefOrGetter<FollowUsersQueryParams>) => {
    return useQuery<FollowUser[], Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return queryKeys.following(p.contactId || '', p.locationId || '', p.limit || 10, p.pageNo || 1)
      }),
      queryFn: async () => {
        const p = toValue(params)
        if (!p.contactId) {
          return []
        }
        if (!p.locationId) {
          throw new Error('Location ID is required for fetching following users')
        }
        return service.getFollowingUsers(p.contactId, p.limit || 10, p.pageNo || 1, p.locationId)
      },
      enabled: computed(() => {
        const p = toValue(params)
        return p.enabled !== false && !!p.contactId && !!p.locationId
      }),
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    })
  }

  const clearUserContributionsQueries = async () => {
    await queryClient.removeQueries({
      queryKey: ['community-user', 'contributions'],
    })
  }

  // User contributions infinite query
  const fetchUserContributionsInfiniteQuery = (params: MaybeRefOrGetter<Omit<UserContributionsQueryParams, 'lastPaginatedId'>>) => {
    const infiniteQuery = useInfiniteQuery<UserContributionsResponse, Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return [
          ...queryKeys.userContributions(
            toValue(p.contactId),
            toValue(p.groupId),
            toValue(p.locationId) || '',
            '', // lastPaginatedId will be handled by pageParam
            toValue(p.limit) || 10,
            toValue(p.contributionType) || 'all'
          ),
          'infinite',
        ]
      }),
      queryFn: async ({ pageParam = '' }) => {
        const p = toValue(params)
        const contactId = toValue(p.contactId)
        const groupId = toValue(p.groupId)
        if (!contactId || !groupId) {
          throw new Error('Contact ID and Group ID are required for fetching user contributions')
        }
        const locationId = toValue(p.locationId)
        if (!locationId) {
          throw new Error('Location ID is required for fetching user contributions')
        }
        const response = await service.getUserContributions(
          contactId,
          groupId,
          locationId,
          toValue(p.limit) || 10,
          pageParam as string,
          toValue(p.contributionType) || 'all'
        )
        return response
      },
      enabled: computed(() => {
        const p = toValue(params)
        return p.enabled !== false && !!p.contactId && !!p.groupId && !!p.locationId
      }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage?.contributions?.length) return undefined
        const totalFetched = allPages.reduce((sum, page) => sum + page.contributions.length, 0)
        const total = lastPage.total ?? 0
        if (total > 0 && totalFetched >= total) return undefined
        const lastContribution = lastPage.contributions[lastPage.contributions.length - 1]
        const fallbackCursor = lastContribution?.token || lastContribution?._id || (lastContribution as { id?: string })?.id
        return lastPage.lastPaginatedId || fallbackCursor || undefined
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    })

    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flatMap(page => page.contributions)
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  const fetchUserActivityStatsQuery = (locationId?: string) => {
    return useQuery<UserActivityStats, Error>({
      queryKey: computed(() => {
        return queryKeys.userActivityStats(locationId)
      }),
      queryFn: async () => service.getUserActivityStats(locationId ?? ''),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => !!locationId),
      refetchOnWindowFocus: false,
    })
  }

  /** Fetch whitelabel (gokollab) contactId for the given portal contactId when isGokollab. */
  const fetchGokollabContactIdQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    contactId: MaybeRefOrGetter<string | undefined>,
    forceCreate: MaybeRefOrGetter<boolean> = true,
    isGokollab: MaybeRefOrGetter<boolean> = false
  ) =>
    useQuery<{ contactId: string }, Error>({
      queryKey: computed(() => {
        const locId = toValue(locationId)
        const cid = toValue(contactId)
        return queryKeys.gokollabContactId(locId ?? '', cid ?? '')
      }),
      queryFn: async () => {
        const locId = toValue(locationId)
        const cid = toValue(contactId)
        if (!locId || !cid) throw new Error('locationId and contactId are required')
        return gokollabService.getGokollabContactId(locId, cid, toValue(forceCreate))
      },
      staleTime: 50 * 60 * 1000,
      gcTime: 100 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(contactId) && !!toValue(isGokollab)),
      refetchOnWindowFocus: false,
    })

  // ===== MUTATION FUNCTIONS =====

  // Follow user mutation
  const followUserMutation = useMutation<void, Error, FollowMutationVariables, FollowStatusMutationContext>({
    mutationFn: async ({ contactId, groupId }) => {
      await service.followUser(contactId, groupId)
    },
    onMutate: async ({ contactId, groupId }) => {
      const key = queryKeys.followStatus(contactId, groupId)
      await queryClient.cancelQueries({ queryKey: key })
      const previousStatus = queryClient.getQueryData<boolean>(key)
      queryClient.setQueryData(key, true)
      return { key, previousStatus }
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previousStatus)
      }
    },
  })

  // Unfollow user mutation
  const unfollowUserMutation = useMutation<void, Error, FollowMutationVariables, FollowStatusMutationContext>({
    mutationFn: async ({ contactId, groupId }) => {
      await service.unfollowUser(contactId, groupId)
    },
    onMutate: async ({ contactId, groupId }) => {
      const key = queryKeys.followStatus(contactId, groupId)
      await queryClient.cancelQueries({ queryKey: key })
      const previousStatus = queryClient.getQueryData<boolean>(key)
      queryClient.setQueryData(key, false)
      return { key, previousStatus }
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previousStatus)
      }
    },
  })

  // Initiate group payment mutation
  const initiateGroupPaymentMutation = useMutation<CommunityUserGroup, Error, GroupPaymentMutationVariables>({
    mutationFn: async ({ groupId, contactId, orderId, orderSourceId, locationId }) => {
      return service.initiateGroupPayment(locationId, groupId, contactId, orderId, orderSourceId)
    },
    onSuccess: (data, variables) => {
      // Invalidate user group details query to refresh membership status
      // Update the community user query cache with the new user group data
      const transformedData = transformToCommunityUser(data as unknown as Record<string, unknown>)

      return updateCommunityUserCache(queryClient, variables.groupId, variables.locationId, transformedData)
    },
  })

  // Capture group payment mutation
  const captureGroupPaymentMutation = useMutation<CommunityUserGroup, Error, CaptureGroupPaymentMutationVariables>({
    mutationFn: async ({ groupId, contactId, orderId, orderSourceId, locationId, useV2 }) => {
      return service.captureGroupPayment(locationId, groupId, contactId, orderId, orderSourceId, useV2)
    },
    onSuccess: (data, variables) => {
      // Invalidate user group details query to refresh membership status
      // Update the community user query cache with the new user group data
      const transformedData = transformToCommunityUser(data as unknown as Record<string, unknown>)

      return updateCommunityUserCache(queryClient, variables.groupId, variables.locationId, transformedData)
    },
  })

  // Join group mutation
  const joinGroupMutation = useMutation<CommunityUserGroup, Error, JoinGroupMutationVariables>({
    mutationFn: async ({ groupId, role = GroupMemberRole.MEMBER, orderId, contactId, status, locationId }) => {
      return service.joinGroup(locationId, groupId, role, orderId, contactId, status)
    },
    onSuccess: (data, variables) => {
      // Invalidate user group details query to refresh membership status
      // Update the community user query cache with the new user group data
      const transformedData = transformToCommunityUser(data as unknown as Record<string, unknown>)

      return updateCommunityUserCache(queryClient, variables.groupId, variables.locationId, transformedData)
    },
  })

  const createInitiateGroupPaymentMutation = initiateGroupPaymentMutation
  const createCaptureGroupPaymentMutation = captureGroupPaymentMutation

  return {
    // Query creation functions
    fetchUserDetailsBySlugQuery,
    fetchCurrentUserDetailsForGroupQuery,
    fetchUserDetailsForGroupByContactQuery,
    fetchUserGroupsInfiniteQuery,
    fetchUserAccessibleGroupsQuery,
    fetchCommonGroupsQuery,
    fetchFollowStatusQuery,
    fetchFollowersQuery,
    fetchFollowingQuery,
    clearUserContributionsQueries,
    fetchUserContributionsInfiniteQuery,
    fetchUserActivityStatsQuery,

    // Mutation creation functions
    followUserMutation,
    unfollowUserMutation,
    initiateGroupPaymentMutation,
    captureGroupPaymentMutation,
    createInitiateGroupPaymentMutation,
    createCaptureGroupPaymentMutation,
    joinGroupMutation,

    // Query keys for manual invalidation
    queryKeys,

    fetchGokollabContactIdQuery,
  }
}
